import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Divider,
  Form,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
  Statistic,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CalculatorOutlined,
  CheckCircleOutlined,
  ExperimentOutlined,
} from "@ant-design/icons";
import { calculateProductionCapacity } from "./api";

const { Text } = Typography;

const formatQuantity = (value, maximumFractionDigits = 2) =>
  new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits,
  }).format(Number(value) || 0);

const getMaterialStock = (material) => {
  const stock = Number(material?.current_stock ?? material?.available);
  return Number.isFinite(stock) ? Math.max(stock, 0) : 0;
};

const ProductionCapacityCalculator = ({
  open,
  onCancel,
  onApply,
  unit,
  companyId,
  warehouseId,
  terminology,
}) => {
  const [mode, setMode] = useState("stock");
  const [materialProductId, setMaterialProductId] = useState(null);
  const [materialQuantity, setMaterialQuantity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const materials = useMemo(() => {
    const materialsByProductId = new Map();
    (Array.isArray(unit?.materials) ? unit.materials : [])
      .filter((material) => Number(material?.quantity) > 0)
      .forEach((material) => {
        const productId = Number(material?.product_id);
        if (productId > 0 && !materialsByProductId.has(productId)) {
          materialsByProductId.set(productId, material);
        }
      });
    return Array.from(materialsByProductId.values());
  }, [unit]);
  const selectedMaterial = useMemo(
    () =>
      materials.find(
        (material) =>
          Number(material.product_id) === Number(materialProductId)
      ) || null,
    [materialProductId, materials]
  );
  const outputUnit =
    result?.output?.unit_short_name ||
    unit?.outputs?.[0]?.unit_short_name ||
    "unités";
  const materialLabel =
    terminology?.materialSingular || "matière première";
  const materialPluralLabel =
    terminology?.materialPlural || "matières premières";
  const materialIndefiniteLabel =
    terminology?.materialIndefiniteSingular || "une matière première";
  const materialPartitiveLabel =
    terminology?.materialPartitiveSingular || "d'une matière première";
  const materialOfAtLeastOneLabel =
    terminology?.materialOfAtLeastOne || "d'au moins une matière première";
  const materialTitle =
    materialLabel.charAt(0).toUpperCase() + materialLabel.slice(1);

  useEffect(() => {
    if (!open) {
      return;
    }

    const firstMaterial = materials[0] || null;
    setMode("stock");
    setMaterialProductId(firstMaterial?.product_id || null);
    setMaterialQuantity(
      firstMaterial ? getMaterialStock(firstMaterial) : null
    );
    setResult(null);
  }, [open, unit?.id, materials]);

  const handleModeChange = (event) => {
    const nextMode = event.target.value;
    setMode(nextMode);
    setResult(null);

    if (nextMode === "material" && selectedMaterial) {
      setMaterialQuantity(getMaterialStock(selectedMaterial));
    }
  };

  const handleMaterialChange = (productId) => {
    const nextMaterial =
      materials.find(
        (material) => Number(material.product_id) === Number(productId)
      ) || null;
    setMaterialProductId(productId);
    setMaterialQuantity(
      nextMaterial ? getMaterialStock(nextMaterial) : null
    );
    setResult(null);
  };

  const handleCalculate = async () => {
    if (!unit?.id) {
      message.error(
        `Veuillez choisir une ${terminology?.unitSingular || "unité de production"}.`
      );
      return;
    }

    if (
      mode === "material" &&
      (!materialProductId || !(Number(materialQuantity) > 0))
    ) {
      message.warning(
        `Choisissez ${materialIndefiniteLabel} et saisissez une quantité supérieure à zéro.`
      );
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const data = await calculateProductionCapacity({
        production_unit_id: unit.id,
        mode,
        material_product_id:
          mode === "material" ? materialProductId : undefined,
        material_quantity:
          mode === "material" ? Number(materialQuantity) : undefined,
        warehouse_id: warehouseId,
        company_id: companyId,
        __offline_unit_snapshot: unit,
      });
      setResult(data);
    } catch (error) {
      const errorMessage =
        error?.error ||
        error?.message ||
        (typeof error === "string" ? error : null) ||
        "Erreur lors du calcul de la production possible.";
      message.error(
        errorMessage
      );
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    const quantity = Number(result?.recommended_output_quantity);
    if (!(quantity > 0) || !onApply) {
      return;
    }

    await Promise.resolve(onApply(quantity, result));
    onCancel?.();
  };

  const limitingNames = (result?.limiting_materials || [])
    .map((material) => material.product_name)
    .filter(Boolean);
  const selectedResultMaterial = result?.selected_material || null;
  const recommendedQuantity = Number(
    result?.recommended_output_quantity
  );
  const theoreticalQuantity = Number(
    result?.theoretical_output_quantity
  );

  return (
    <Modal
      title={
        <Space>
          <CalculatorOutlined />
          Calculer selon mes {materialPluralLabel}
        </Space>
      }
      open={open}
      onCancel={onCancel}
      footer={null}
      destroyOnClose
      width={680}
      maskClosable={!loading}
    >
      <Text type="secondary">
        Estimez ce que vous pouvez obtenir avant de revenir au calcul normal
        des besoins.
      </Text>

      <Form layout="vertical" style={{ marginTop: 20 }}>
        <Form.Item label="Calculer à partir de">
          <Radio.Group value={mode} onChange={handleModeChange}>
            <Space direction="vertical">
              <Radio value="stock">Tous les stocks actuels</Radio>
              <Radio value="material">
                Une quantité précise {materialPartitiveLabel}
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        {mode === "material" && (
          <Space
            align="start"
            size={12}
            wrap
            style={{ display: "flex", width: "100%" }}
          >
            <Form.Item
              label={materialTitle}
              style={{ flex: "1 1 260px", marginBottom: 12 }}
            >
              <Select
                value={materialProductId}
                onChange={handleMaterialChange}
                placeholder={`Choisir ${materialIndefiniteLabel}`}
                showSearch
                optionFilterProp="label"
                options={materials.map((material) => ({
                  value: material.product_id,
                  label: material.product_name,
                }))}
              />
            </Form.Item>
            <Form.Item
              label="Quantité disponible"
              style={{ flex: "1 1 210px", marginBottom: 12 }}
            >
              <InputNumber
                min={0.01}
                step={0.01}
                value={materialQuantity}
                onChange={(value) => {
                  setMaterialQuantity(value);
                  setResult(null);
                }}
                addonAfter={selectedMaterial?.unit_short_name || "unité"}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Space>
        )}

        {mode === "material" && selectedMaterial && (
          <Text type="secondary">
            Stock actuel : {formatQuantity(getMaterialStock(selectedMaterial))}{" "}
            {selectedMaterial.unit_short_name || ""}
          </Text>
        )}

        <div style={{ marginTop: 20 }}>
          <Button
            type="primary"
            icon={<CalculatorOutlined />}
            loading={loading}
            onClick={handleCalculate}
          >
            Calculer la production possible
          </Button>
        </div>
      </Form>

      {result && (
        <>
          <Divider />

          {result.local_estimate && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Estimation hors ligne"
              description={result.offline_warning}
            />
          )}

          {recommendedQuantity > 0 ? (
            <>
              <Alert
                type={
                  result.is_output_capped
                    ? "warning"
                    : result.is_fully_achievable
                    ? "success"
                    : "warning"
                }
                showIcon
                icon={
                  result.is_fully_achievable &&
                  !result.is_output_capped ? (
                    <CheckCircleOutlined />
                  ) : undefined
                }
                message={
                  result.is_output_capped
                    ? "Capacité supérieure à la limite d’un lancement"
                    : mode === "stock"
                    ? "Production maximale avec les stocks actuels"
                    : result.is_fully_achievable
                    ? "Quantité réalisable avec les stocks actuels"
                    : "Les autres stocks limitent cette production"
                }
                description={
                  result.is_output_capped ? (
                    <span>
                      La capacité calculée est de{" "}
                      {formatQuantity(
                        result.uncapped_recommended_output_quantity
                      )}{" "}
                      {outputUnit}. Un lancement est limité à{" "}
                      {formatQuantity(result.maximum_output_quantity)}{" "}
                      {outputUnit} : fractionnez la production en plusieurs
                      lancements.
                    </span>
                  ) : mode === "material" &&
                  !result.is_fully_achievable ? (
                    <span>
                      Avec{" "}
                      {formatQuantity(
                        selectedResultMaterial?.entered_quantity
                      )}{" "}
                      {selectedResultMaterial?.unit_short_name || ""} de{" "}
                      {selectedResultMaterial?.product_name || materialLabel},
                      le rendement théorique est de{" "}
                      {formatQuantity(theoreticalQuantity)} {outputUnit}.
                      Les autres stocks permettent actuellement{" "}
                      {formatQuantity(recommendedQuantity)} {outputUnit}.
                    </span>
                  ) : mode === "material" ? (
                    <span>
                      La quantité saisie permet de produire{" "}
                      {formatQuantity(recommendedQuantity)} {outputUnit}.
                    </span>
                  ) : (
                    <span>
                      Cette quantité tient compte de tous les{" "}
                      {materialPluralLabel} disponibles dans l'établissement.
                    </span>
                  )
                }
                style={{ marginBottom: 16 }}
              />

              <Space
                align="center"
                wrap
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 16,
                }}
              >
                <Statistic
                  title="Quantité recommandée"
                  value={recommendedQuantity}
                  precision={2}
                  suffix={outputUnit}
                />
                {(result.is_output_capped || limitingNames.length > 0) && (
                  <div>
                    <Text type="secondary">Facteur(s) limitant(s)</Text>
                    <div style={{ marginTop: 6 }}>
                      {result.is_output_capped ? (
                        <Tag color="blue">Limite par lancement</Tag>
                      ) : (
                        limitingNames.map((name) => (
                          <Tag color="gold" key={name}>
                            {name}
                          </Tag>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </Space>

              {selectedResultMaterial?.entered_quantity_exceeds_stock && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginTop: 16 }}
                  message="La quantité saisie dépasse le stock enregistré"
                  description={`Le stock actuel de ${
                    selectedResultMaterial.product_name
                  } est de ${formatQuantity(
                    selectedResultMaterial.current_stock
                  )} ${selectedResultMaterial.unit_short_name || ""}.`}
                />
              )}

              <Divider />
              <Space
                align="center"
                wrap
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                }}
              >
                <Text type="secondary">
                  Le stock sera revérifié avant le lancement.
                </Text>
                <Button
                  type="primary"
                  icon={<ExperimentOutlined />}
                  onClick={handleApply}
                >
                  Utiliser {formatQuantity(recommendedQuantity)} {outputUnit}
                </Button>
              </Space>
            </>
          ) : (
            <Alert
              type="error"
              showIcon
              message="Aucune production possible"
              description={`Le stock ${materialOfAtLeastOneLabel} est insuffisant pour produire 0,01 ${outputUnit}.`}
            />
          )}
        </>
      )}
    </Modal>
  );
};

export default ProductionCapacityCalculator;
